import type { ReactNode } from 'react';
import {
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';

interface Props<T extends string> {
  icon: ReactNode;
  /** The view this toggle owns. Pressing it opens that view, or closes it. */
  view: T;
  openView: T | undefined;
  size?: 'default' | 'sm' | 'lg';
  onToggle: (view: T) => void;
  /** Announced while this view is open, i.e. what pressing it will do. */
  hideLabel: string;
  /** Announced while it is closed. */
  showLabel: string;
  /**
   * Visible text beside the icon. `showLabel` / `hideLabel` stay the announced
   * name, so a caller adding one says the same thing at length there.
   */
  label?: string;
  /** `outline` for a toggle standing on its own rather than in a group of them. */
  variant?: 'default' | 'outline';
  /** Merged onto the `Toggle`, for a host placing it on a coloured surface. */
  className?: string;
  dataTestId?: string;
}

/**
 * Header affordance for one `DetailSidebar` view. Built on the design system's
 * `Toggle` so the pressed state is the shared one, rather than a background
 * colour painted on a ghost button.
 *
 * `lg` rather than the default: detail headers run 40px controls throughout, so
 * the default 36px toggle would sit short against the buttons beside it.
 *
 * The tooltip repeats the aria label rather than carrying copy of its own, so a
 * sighted user reads exactly what a screen reader announces. It mounts its own
 * provider because Radix throws rather than degrading when none is in scope,
 * and this toggle is a slot its hosts can place anywhere.
 */
export function DetailSidebarViewToggle<T extends string>(props: Props<T>) {
  const isOpen = props.openView === props.view;
  const label = isOpen ? props.hideLabel : props.showLabel;

  return (
    <TooltipProvider>
      <Tooltip>
        {/* The trigger takes a wrapper, not the Toggle itself: `asChild` on the
            Toggle would overwrite its `data-state` with the tooltip's own
            open/closed, and the design system paints the pressed state from
            `data-[state=on]`. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Toggle
              size={props.size ?? 'lg'}
              variant={props.variant}
              className={props.className}
              pressed={isOpen}
              onPressedChange={() => props.onToggle(props.view)}
              aria-label={label}
              data-testid={props.dataTestId}
            >
              {props.icon}
              {props.label ? <span aria-hidden>{props.label}</span> : null}
            </Toggle>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
